import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Download, FileUp, Loader2, Search, Server } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import {
  ROTULOS_TIPO,
  type ItemPreviaSigtap,
  type PreviaSigtap,
  type ResultadoImportacaoSigtap,
  type SituacaoItemSigtap,
  type TabelaSigtapDisponivel,
} from '@/features/admin/tipos';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

/**
 * Teto de linhas desenhadas de uma vez.
 *
 * Uma competência tem quase 5 mil procedimentos, e pintar tudo trava a janela.
 * A seleção continua valendo sobre o filtro inteiro — o corte é só do que
 * aparece na tela.
 */
const MAXIMO_VISIVEL = 150;

const ROTULOS_SITUACAO: Record<SituacaoItemSigtap, string> = {
  NOVO: 'Novo',
  ATUALIZAR: 'Atualizar',
  SEM_MUDANCA: 'Já cadastrado',
};

const TONS_SITUACAO: Record<SituacaoItemSigtap, 'sucesso' | 'alerta' | 'neutro'> = {
  NOVO: 'sucesso',
  ATUALIZAR: 'alerta',
  SEM_MUDANCA: 'neutro',
};

const normalizar = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

/** `202607` → `07/2026`, que é como a competência é lida em serviço. */
function competenciaAmigavel(competencia: string): string {
  return /^\d{6}$/.test(competencia)
    ? `${competencia.slice(4, 6)}/${competencia.slice(0, 4)}`
    : competencia;
}

type Origem = 'FTP' | 'ARQUIVO';

interface ImportarProcedimentosSigtapProps {
  aberto: boolean;
  aoFechar: () => void;
}

/**
 * Importação de procedimentos pela Tabela Unificada (SIGTAP) do DATASUS.
 *
 * Dois caminhos para o mesmo arquivo: baixar do FTP público ou enviar o ZIP. O
 * segundo existe porque a rede do servidor pode bloquear FTP de saída — e aí o
 * cadastro não pode ficar refém disso.
 */
export function ImportarProcedimentosSigtap({
  aberto,
  aoFechar,
}: ImportarProcedimentosSigtapProps) {
  const queryClient = useQueryClient();

  const [origem, setOrigem] = React.useState<Origem>('FTP');
  const [competencia, setCompetencia] = React.useState('');
  const [arquivo, setArquivo] = React.useState<File | null>(null);
  const [previa, setPrevia] = React.useState<PreviaSigtap | null>(null);
  const [busca, setBusca] = React.useState('');
  const [gruposVisiveis, setGruposVisiveis] = React.useState<Set<string>>(new Set());
  const [escolhidos, setEscolhidos] = React.useState<Set<string>>(new Set());
  const [duplicados, setDuplicados] = React.useState<{ codigo: string; nome: string }[]>([]);

  const competencias = useQuery({
    queryKey: ['sigtap-competencias'],
    queryFn: () => api.get<{ competencias: TabelaSigtapDisponivel[] }>('/sigtap/competencias'),
    enabled: aberto && origem === 'FTP',
    // O FTP do DATASUS é serviço público de terceiros: consultar de novo a cada
    // foco na janela seria abuso.
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 60 * 60_000,
  });

  // A mais recente já vem escolhida: é o que se quer em quase toda importação.
  React.useEffect(() => {
    const primeira = competencias.data?.competencias[0]?.competencia;
    if (primeira && !competencia) setCompetencia(primeira);
  }, [competencias.data, competencia]);

  React.useEffect(() => {
    if (aberto) return;
    setOrigem('FTP');
    setCompetencia('');
    setArquivo(null);
    setPrevia(null);
    setBusca('');
    setGruposVisiveis(new Set());
    setEscolhidos(new Set());
    setDuplicados([]);
  }, [aberto]);

  const aoReceberPrevia = (resposta: PreviaSigtap) => {
    setPrevia(resposta);
    setDuplicados([]);
    // Grupos sugeridos ligados, e a seleção começa por eles: o resto da tabela
    // fica acessível, mas sem entrar no catálogo por inércia.
    setGruposVisiveis(
      new Set(resposta.grupos.filter((grupo) => grupo.sugerido).map((grupo) => grupo.codigo)),
    );
    setEscolhidos(
      new Set(
        resposta.itens
          .filter((item) => item.sugerido && item.situacao !== 'SEM_MUDANCA')
          .map((item) => item.codigo),
      ),
    );
  };

  const consultar = useMutation({
    mutationFn: () => {
      if (origem === 'ARQUIVO') {
        const form = new FormData();
        form.append('arquivo', arquivo!);
        return api.postForm<PreviaSigtap>('/sigtap/previa/arquivo', form);
      }
      return api.post<PreviaSigtap>('/sigtap/previa', { competencia });
    },
    onSuccess: aoReceberPrevia,
    onError: (erro) => {
      setPrevia(null);
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível ler a Tabela Unificada.',
      );
    },
  });

  const importar = useMutation({
    mutationFn: () => {
      const codigos = [...escolhidos];

      if (origem === 'ARQUIVO') {
        const form = new FormData();
        // O arquivo vai de novo: o servidor relê o pacote em vez de aceitar
        // nome e código vindos da tela.
        form.append('arquivo', arquivo!);
        form.append('codigos', JSON.stringify(codigos));
        return api.postForm<ResultadoImportacaoSigtap>('/sigtap/importar/arquivo', form);
      }

      return api.post<ResultadoImportacaoSigtap>('/sigtap/importar', { competencia, codigos });
    },
    onSuccess: async (resultado) => {
      toast.success(
        `${resultado.criados} cadastrado(s), ${resultado.atualizados} atualizado(s), ` +
          `${resultado.inalterados} sem mudança.`,
      );
      await queryClient.invalidateQueries({ queryKey: ['procedimentos'] });
      await queryClient.invalidateQueries({ queryKey: ['procedimentos-ativos'] });

      // Nome repetido no SIGTAP não cabe num toast: fica na tela para o
      // administrador decidir o que fazer com o que sobrou.
      if (resultado.duplicados.length > 0) {
        setDuplicados(resultado.duplicados);
        return;
      }

      aoFechar();
    },
    onError: (erro) => {
      toast.error(
        erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível importar do SIGTAP.',
      );
    },
  });

  const itens = React.useMemo(() => previa?.itens ?? [], [previa]);

  const filtrados = React.useMemo(() => {
    const termo = normalizar(busca.trim());

    return itens.filter((item) => {
      if (gruposVisiveis.size > 0 && !gruposVisiveis.has(item.grupo)) return false;
      if (!termo) return true;
      return (
        normalizar(item.nome).includes(termo) ||
        item.codigo.includes(termo) ||
        normalizar(item.subgrupoNome).includes(termo)
      );
    });
  }, [itens, busca, gruposVisiveis]);

  const alternarGrupo = (codigo: string) => {
    setGruposVisiveis((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(codigo)) proximo.delete(codigo);
      else proximo.add(codigo);
      return proximo;
    });
  };

  const alternarItem = (codigo: string) => {
    setEscolhidos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(codigo)) proximo.delete(codigo);
      else proximo.add(codigo);
      return proximo;
    });
  };

  const marcar = (alvos: ItemPreviaSigtap[]) =>
    setEscolhidos((atual) => new Set([...atual, ...alvos.map((item) => item.codigo)]));

  const podeConsultar = origem === 'ARQUIVO' ? Boolean(arquivo) : /^\d{6}$/.test(competencia);
  const ocultos = Math.max(0, filtrados.length - MAXIMO_VISIVEL);

  return (
    <Dialogo open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogoConteudo className="max-w-3xl">
        <DialogoCabecalho>
          <DialogoTitulo>Importar procedimentos do SIGTAP</DialogoTitulo>
          <DialogoDescricao>
            Tabela Unificada do DATASUS. Baixe a competência direto do FTP público ou envie o ZIP.
            Nada é gravado até você confirmar.
          </DialogoDescricao>
        </DialogoCabecalho>

        <DialogoCorpo>
          <div className="flex flex-wrap gap-2">
            <Botao
              variante={origem === 'FTP' ? 'primario' : 'contorno'}
              tamanho="sm"
              onClick={() => {
                setOrigem('FTP');
                setPrevia(null);
              }}
            >
              <Server aria-hidden="true" />
              Baixar do DATASUS
            </Botao>
            <Botao
              variante={origem === 'ARQUIVO' ? 'primario' : 'contorno'}
              tamanho="sm"
              onClick={() => {
                setOrigem('ARQUIVO');
                setPrevia(null);
              }}
            >
              <FileUp aria-hidden="true" />
              Enviar arquivo
            </Botao>
          </div>

          <div className="flex flex-wrap items-end gap-2">
            {origem === 'FTP' ? (
              <Campo
                id="sigtap-competencia"
                rotulo="Competência"
                className="min-w-56 flex-1"
                ajuda={
                  competencias.isError
                    ? undefined
                    : 'Publicações do DATASUS, da mais recente para a mais antiga.'
                }
                erro={
                  competencias.isError
                    ? competencias.error instanceof ErroApi
                      ? competencias.error.mensagemUsuario
                      : 'Não foi possível listar as competências.'
                    : undefined
                }
              >
                <Selecao
                  value={competencia}
                  onChange={(e) => {
                    setCompetencia(e.target.value);
                    setPrevia(null);
                  }}
                  disabled={competencias.isPending || competencias.isError}
                >
                  {competencias.isPending && <option value="">Consultando o DATASUS…</option>}
                  {competencias.data?.competencias.map((tabela) => (
                    <option key={tabela.competencia} value={tabela.competencia}>
                      {competenciaAmigavel(tabela.competencia)}
                      {tabela.versao ? ` — versão ${tabela.versao}` : ''}
                    </option>
                  ))}
                </Selecao>
              </Campo>
            ) : (
              <Campo
                id="sigtap-arquivo"
                rotulo="Arquivo da Tabela Unificada"
                className="min-w-56 flex-1"
                ajuda="ZIP como o DATASUS publica, sem recompactar."
              >
                <Entrada
                  type="file"
                  accept=".zip,application/zip"
                  className="file:text-silenciado-texto h-auto py-1.5 file:mr-3 file:rounded file:border-0 file:text-sm"
                  onChange={(e) => {
                    setArquivo(e.target.files?.[0] ?? null);
                    setPrevia(null);
                  }}
                />
              </Campo>
            )}

            <Botao
              variante="contorno"
              onClick={() => consultar.mutate()}
              disabled={!podeConsultar || consultar.isPending}
            >
              {consultar.isPending ? (
                <Loader2 className="animate-spin" aria-hidden="true" />
              ) : (
                <Search aria-hidden="true" />
              )}
              {consultar.isPending && origem === 'FTP' ? 'Baixando…' : 'Ver prévia'}
            </Botao>
          </div>

          {previa && (
            <>
              <div className="border-borda rounded-lg border p-3 text-sm">
                <p className="font-medium">
                  Competência {competenciaAmigavel(previa.competencia)}
                  <span className="text-silenciado-texto font-normal">
                    {previa.origem === 'FTP' ? ' · baixada do DATASUS' : ' · do arquivo enviado'}
                  </span>
                </p>
                {previa.arquivo && (
                  <p className="text-silenciado-texto text-xs break-all">{previa.arquivo}</p>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <Etiqueta tom="sucesso">{previa.resumo.novos} novos</Etiqueta>
                  <Etiqueta tom="alerta">{previa.resumo.atualizar} a atualizar</Etiqueta>
                  <Etiqueta>{previa.resumo.semMudanca} já cadastrados</Etiqueta>
                  <Etiqueta tom="marca">{previa.resumo.total} na tabela</Etiqueta>
                </div>
              </div>

              <fieldset className="flex flex-col gap-1.5">
                <legend className="text-sm font-medium">Grupos do SIGTAP</legend>
                <p className="text-silenciado-texto text-xs">
                  Diagnóstica e clínicos vêm ligados — são os que viram exame e consulta no
                  agendamento. Ligue outro grupo para vê-lo na lista.
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {previa.grupos.map((grupo) => (
                    <label
                      key={grupo.codigo}
                      className="border-borda hover:bg-silenciado flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 text-xs"
                    >
                      <input
                        type="checkbox"
                        className="accent-marca size-3.5"
                        checked={gruposVisiveis.has(grupo.codigo)}
                        onChange={() => alternarGrupo(grupo.codigo)}
                      />
                      <span>
                        {grupo.nome}
                        <span className="text-silenciado-texto"> ({grupo.total})</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative min-w-48 flex-1">
                  <Search
                    className="text-silenciado-texto pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
                    aria-hidden="true"
                  />
                  <Entrada
                    className="pl-9"
                    placeholder="Buscar por nome, código ou subgrupo"
                    aria-label="Buscar na tabela do SIGTAP"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                  />
                </div>
                <Botao variante="contorno" tamanho="sm" onClick={() => marcar(filtrados)}>
                  Marcar todos do filtro
                </Botao>
                <Botao variante="fantasma" tamanho="sm" onClick={() => setEscolhidos(new Set())}>
                  Limpar
                </Botao>
              </div>

              <p className="text-silenciado-texto text-xs">
                {escolhidos.size} selecionado(s). O tipo de cada item — consulta, exame ou
                procedimento — sai da estrutura do código SIGTAP.
              </p>

              <ul className="border-borda max-h-72 overflow-y-auto rounded-lg border">
                {filtrados.length === 0 && (
                  <li className="text-silenciado-texto p-3 text-sm">
                    Nenhum procedimento corresponde ao filtro.
                  </li>
                )}

                {filtrados.slice(0, MAXIMO_VISIVEL).map((item) => (
                  <li key={item.codigo} className="border-borda border-b last:border-b-0">
                    <label className="hover:bg-silenciado flex cursor-pointer items-start gap-3 p-2.5">
                      <input
                        type="checkbox"
                        className="accent-marca mt-1 size-4 shrink-0"
                        checked={escolhidos.has(item.codigo)}
                        onChange={() => alternarItem(item.codigo)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{item.nome}</span>
                          <Etiqueta tom={TONS_SITUACAO[item.situacao]}>
                            {ROTULOS_SITUACAO[item.situacao]}
                          </Etiqueta>
                          <Etiqueta tom="marca">{ROTULOS_TIPO[item.tipo]}</Etiqueta>
                        </span>
                        <span className="text-silenciado-texto block text-xs">
                          {item.codigo} · {item.subgrupoNome}
                        </span>
                        {item.mudancas.length > 0 && (
                          <span className="text-silenciado-texto block text-xs italic">
                            {item.mudancas.join(' · ')}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                ))}

                {ocultos > 0 && (
                  <li className="text-silenciado-texto bg-silenciado p-2.5 text-xs">
                    Mostrando {MAXIMO_VISIVEL} de {filtrados.length}. Refine a busca para ver o
                    resto — &quot;Marcar todos do filtro&quot; vale sobre todos os{' '}
                    {filtrados.length}.
                  </li>
                )}
              </ul>

              {duplicados.length > 0 && (
                <div
                  role="alert"
                  className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto rounded-md border p-3 text-sm"
                >
                  <p>
                    {duplicados.length} item(ns) não entraram porque o SIGTAP repete o nome com
                    outro código, e o catálogo aceita um por nome e tipo:
                  </p>
                  <ul className="mt-1 list-inside list-disc text-xs">
                    {duplicados.slice(0, 5).map((duplicado) => (
                      <li key={duplicado.codigo}>
                        {duplicado.codigo} — {duplicado.nome}
                      </li>
                    ))}
                  </ul>
                  {duplicados.length > 5 && (
                    <p className="mt-1 text-xs">e mais {duplicados.length - 5}.</p>
                  )}
                </div>
              )}
            </>
          )}

          {!previa && !consultar.isPending && (
            <p className="text-silenciado-texto flex items-start gap-2 text-xs">
              <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />A tabela tem
              cerca de 5 mil procedimentos. A prévia mostra tudo, já marcando apenas os grupos que
              interessam ao agendamento.
            </p>
          )}
        </DialogoCorpo>

        <DialogoRodape>
          <Botao variante="contorno" onClick={aoFechar}>
            {duplicados.length > 0 ? 'Fechar' : 'Cancelar'}
          </Botao>
          <Botao
            onClick={() => importar.mutate()}
            disabled={!previa || escolhidos.size === 0 || importar.isPending}
          >
            {importar.isPending ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : (
              <Download aria-hidden="true" />
            )}
            Importar {escolhidos.size > 0 ? `${escolhidos.size} ` : ''}procedimentos
          </Botao>
        </DialogoRodape>
      </DialogoConteudo>
    </Dialogo>
  );
}
